/**
 * Widget Component Registry
 *
 * Central registry for all widget components used in the dynamic rendering system.
 * Maps widget component names to their actual React component implementations.
 */

import React from 'react';
import PIIMonitoringWidget from './PIIMonitoringWidget';

// ============================================
// PLACEHOLDER WIDGET COMPONENTS
// ============================================

/**
 * Gmail Account Widget - Placeholder
 *
 * Displays Gmail account information and sync status.
 * TODO: Implement full Gmail integration in future iteration.
 */
export const GmailAccountWidget: React.FC<{ recordId?: string }> = ({ recordId }) => (
  <div className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
      Gmail Account
    </h3>
    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
      Gmail widget placeholder - Integration coming soon
    </p>
    {recordId && (
      <p className="text-xs text-gray-500 dark:text-gray-500">
        Record ID: {recordId}
      </p>
    )}
    <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
      <p className="text-sm text-blue-800 dark:text-blue-200">
        📧 This widget will display Gmail account status, last sync time, and account health.
      </p>
    </div>
  </div>
);

// PII Monitoring Widget is imported from ./PIIMonitoringWidget.tsx
// Re-export for consistency
export { default as PIIMonitoringWidget } from './PIIMonitoringWidget';

// ============================================
// WIDGET REGISTRY
// ============================================

/**
 * Widget Registry
 *
 * Maps widget component names (as strings) to their React component implementations.
 * Add new widgets here as they are developed.
 */
export const widgetRegistry: Record<string, React.ComponentType<any>> = {
  GmailAccountWidget,
  PIIMonitoringWidget,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get Widget Component by Name
 *
 * Looks up a widget component by its name in the registry.
 * Returns undefined if the widget is not found.
 *
 * @param name - The name of the widget component
 * @returns The widget component or undefined
 *
 * @example
 * ```tsx
 * const Widget = getWidget('GmailAccountWidget');
 * if (Widget) {
 *   return <Widget recordId="123" />;
 * }
 * ```
 */
export const getWidget = (name: string): React.ComponentType<any> | undefined => {
  return widgetRegistry[name];
};

/**
 * Check if Widget Exists
 *
 * Checks if a widget component exists in the registry.
 *
 * @param name - The name of the widget component
 * @returns True if the widget exists, false otherwise
 *
 * @example
 * ```tsx
 * if (widgetExists('GmailAccountWidget')) {
 *   console.log('Gmail widget is available');
 * }
 * ```
 */
export const widgetExists = (name: string): boolean => {
  return name in widgetRegistry;
};

/**
 * Get All Widget Names
 *
 * Returns an array of all registered widget names.
 * Useful for debugging and configuration UIs.
 *
 * @returns Array of widget names
 *
 * @example
 * ```tsx
 * const widgets = getAllWidgetNames();
 * console.log('Available widgets:', widgets);
 * ```
 */
export const getAllWidgetNames = (): string[] => {
  return Object.keys(widgetRegistry);
};
